/**
 * HEIC/HEIF detection + client-side conversion to JPEG.
 *
 * iPhone photos default to HEIC, which the browser canvas can't decode (Safari
 * is the only exception). Without this helper, an iPhone user picking a photo
 * straight from Photos hits createImageBitmap() and silently fails.
 *
 * heic2any is lazy-imported so the ~600KB libheif bundle only ships when a
 * HEIC file is actually picked.
 */

const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif'])
const HEIC_EXTENSIONS = ['.heic', '.heif']

export function isHeicFile(file: File): boolean {
  if (file.type && HEIC_MIME_TYPES.has(file.type.toLowerCase())) return true
  // iOS occasionally hands us a HEIC file with an empty type. Fall back to
  // the extension so we still catch it.
  const name = file.name.toLowerCase()
  return HEIC_EXTENSIONS.some((ext) => name.endsWith(ext))
}

// ISO-BMFF `ftyp` brands that indicate HEIC/HEIF. The container starts with a
// 4-byte box size, then "ftyp" (bytes 4-7), then the major brand (bytes 8-11).
const HEIC_FTYP_BRANDS = new Set([
  'heic', 'heix', 'heif', 'hevc', 'hevx', 'mif1', 'msf1',
])

/**
 * Detect HEIC/HEIF by reading the file's magic bytes, for the case where both
 * the MIME type and extension are missing or wrong. This happens on Android
 * when an iOS-origin HEIC is shared across apps: the picker hands us an empty
 * `type` and a generic name, so `isHeicFile()` misses it and the file dies in
 * `createImageBitmap()` as an "INVALID_IMAGE". Reads only the first 16 bytes.
 */
export async function isHeicByMagicBytes(file: File): Promise<boolean> {
  try {
    const header = new Uint8Array(await file.slice(0, 16).arrayBuffer())
    if (header.length < 12) return false
    // bytes 4-7 must spell "ftyp"
    if (header[4] !== 0x66 || header[5] !== 0x74 || header[6] !== 0x79 || header[7] !== 0x70) {
      return false
    }
    const brand = String.fromCharCode(header[8], header[9], header[10], header[11]).toLowerCase()
    return HEIC_FTYP_BRANDS.has(brand)
  } catch {
    return false
  }
}

export async function convertHeicToJpeg(
  file: File,
  quality: number = 0.92
): Promise<File> {
  const { default: heic2any } = await import('heic2any')

  const result = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality,
  })

  // heic2any returns Blob | Blob[] depending on input. Normalize.
  const blob = Array.isArray(result) ? result[0] : result
  if (!blob) {
    throw new Error('HEIC conversion produced no output')
  }

  const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg') || 'image.jpg'
  return new File([blob], newName.endsWith('.jpg') ? newName : `${newName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  })
}
